//@flow
import React from 'react';
import classnames from 'classnames';
import theme from './FpTimelineScale.scss';
import moment from 'moment-timezone';
import { themr } from 'react-css-themr';

type Props = {
    visibleTimeStart: number,
    visibleTimeEnd: number,
    scale: number,
    scaleFactor: number,
    width: number,
    showCurrentTimeIndicator?: boolean,
    showScaleLineIndicator?: boolean,
    theme: Object
};
class FpTimelineScale extends React.PureComponent<Props, void> {
    pxToTime = (px: number) => {
        const { visibleTimeStart, scale, scaleFactor } = this.props;
        const effectiveScale = scale * (1 / scaleFactor);
        return px * effectiveScale + visibleTimeStart;
    };

    timeToPx = (time: number) => {
        const { visibleTimeStart, scale, scaleFactor } = this.props;
        const effectiveScale = scale * (1 / scaleFactor);
        return (time - visibleTimeStart) / effectiveScale;
    };

    renderScaleLines() {
        const {
            visibleTimeStart,
            visibleTimeEnd,
            scale,
            scaleFactor,
            width,
            showCurrentTimeIndicator,
            showScaleLineIndicator,
            theme
        } = this.props;
        const scaleLines = [];
        const scaleWidth = 60 * scale;
        const effectiveScale = scale * (1 / scaleFactor);
        let timePoint = moment(visibleTimeStart)
            .minute(0)
            .second(0)
            .millisecond(0);
        let timePointValue = timePoint.valueOf();
        let prevTimePoint = null;

        const currentTime = moment().second(0).millisecond(0).valueOf();

        if (
            visibleTimeEnd >= currentTime &&
            currentTime >= visibleTimeStart &&
            showCurrentTimeIndicator
        ) {
            const currentPx = (currentTime - visibleTimeStart) / effectiveScale;
            scaleLines.push(
                <div
                    key={'current-time-scale-line'}
                    className={classnames(theme.scaleLabel, theme.currentTime)}
                    style={{
                        position: 'absolute',
                        left: currentPx,
                        width: '1px',
                        textAlign: 'center'
                    }}
                >
                    <div className={theme.timeArrow} />
                </div>
            );
        }

        let px = 0;
        let widthPx = 0;
        while (timePointValue <= visibleTimeEnd || px + widthPx < width) {
            px = (timePointValue - visibleTimeStart) / effectiveScale;
            widthPx =
                (timePointValue + scaleWidth - visibleTimeStart) /
                    effectiveScale -
                px;
            // The div should start from half of the prev to half of the next
            px = px - widthPx / 2;
            scaleLines.push(
                <div
                    key={timePointValue}
                    className={theme.scaleLabel}
                    style={{
                        position: 'absolute',
                        left: px,
                        width: widthPx,
                        textAlign: 'center'
                    }}
                >
                    <span>{timePoint.format('hh:mm A')}</span>
                    {showScaleLineIndicator ? (
                        <div className={theme.scaleIndicator} />
                    ) : null}
                </div>
            );
            if (
                px > 0 &&
                prevTimePoint &&
                !timePoint.isSame(prevTimePoint, 'day')
            ) {
                scaleLines.push(
                    <div
                        key={`${timePointValue}-date`}
                        className={classnames(theme.date, theme.scaleLabel)}
                        style={{
                            position: 'absolute',
                            left: px + widthPx / 2,
                            width: 'auto',
                            textAlign: 'center'
                        }}
                    >
                        {timePoint.format('D MMM')}
                    </div>
                );
            }
            prevTimePoint = timePoint;
            timePoint = moment(timePointValue + scaleWidth);
            timePointValue = timePoint.valueOf();
        }
        return scaleLines;
    }

    render() {
        const { theme } = this.props;
        return (
            <div className={theme.scaleContainer}>
                {this.renderScaleLines()}
            </div>
        );
    }
}

export default themr('FpTimelineScale', theme, { withRef: true })(
    FpTimelineScale
);
